/**
 * i18n Configuration
 *
 * Supported languages:
 * - de (German) - Default/Fallback
 * - it (Italian)
 *
 * Namespaces:
 * - common: Buttons, labels, errors, general UI
 * - auth: Login, register, password
 * - dashboard: Dashboard page
 * - posts: Posts, calendar
 * - flights: Campaigns/Flights
 * - creator: Creator Studio
 * - media: Media library
 * - settings: Settings page
 * - admin: Admin area
 * - projects: Projects page
 * - marketing: Marketing planning
 * - analytics: Analytics pages
 * - trash: Trash/Recycle bin
 * - tenants: Tenant management
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';

// German (default)
import commonDe from '../locales/de/common.json';
import authDe from '../locales/de/auth.json';
import dashboardDe from '../locales/de/dashboard.json';
import postsDe from '../locales/de/posts.json';
import flightsDe from '../locales/de/flights.json';
import creatorDe from '../locales/de/creator.json';
import mediaDe from '../locales/de/media.json';
import settingsDe from '../locales/de/settings.json';
import adminDe from '../locales/de/admin.json';
import projectsDe from '../locales/de/projects.json';
import marketingDe from '../locales/de/marketing.json';
import analyticsDe from '../locales/de/analytics.json';
import trashDe from '../locales/de/trash.json';
import tenantsDe from '../locales/de/tenants.json';

// Italian
import commonIt from '../locales/it/common.json';
import authIt from '../locales/it/auth.json';
import dashboardIt from '../locales/it/dashboard.json';
import postsIt from '../locales/it/posts.json';
import flightsIt from '../locales/it/flights.json';
import creatorIt from '../locales/it/creator.json';
import mediaIt from '../locales/it/media.json';
import settingsIt from '../locales/it/settings.json';
import adminIt from '../locales/it/admin.json';
import projectsIt from '../locales/it/projects.json';
import marketingIt from '../locales/it/marketing.json';
import analyticsIt from '../locales/it/analytics.json';
import trashIt from '../locales/it/trash.json';
import tenantsIt from '../locales/it/tenants.json';

export const SUPPORTED_LANGUAGES = [
  { code: 'de', name: 'Deutsch', flag: '🇩🇪' },
  { code: 'it', name: 'Italiano', flag: '🇮🇹' },
] as const;

export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number]['code'];

const resources = {
  de: {
    common: commonDe,
    auth: authDe,
    dashboard: dashboardDe,
    posts: postsDe,
    flights: flightsDe,
    creator: creatorDe,
    media: mediaDe,
    settings: settingsDe,
    admin: adminDe,
    projects: projectsDe,
    marketing: marketingDe,
    analytics: analyticsDe,
    trash: trashDe,
    tenants: tenantsDe,
  },
  it: {
    common: commonIt,
    auth: authIt,
    dashboard: dashboardIt,
    posts: postsIt,
    flights: flightsIt,
    creator: creatorIt,
    media: mediaIt,
    settings: settingsIt,
    admin: adminIt,
    projects: projectsIt,
    marketing: marketingIt,
    analytics: analyticsIt,
    trash: trashIt,
    tenants: tenantsIt,
  },
};

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources,
    fallbackLng: 'de',
    defaultNS: 'common',
    ns: ['common', 'auth', 'dashboard', 'posts', 'flights', 'creator', 'media', 'settings', 'admin', 'projects', 'marketing', 'analytics', 'trash', 'tenants'],

    detection: {
      // Order of language detection
      order: ['localStorage', 'navigator'],
      // Cache language in localStorage
      caches: ['localStorage'],
      lookupLocalStorage: 'i18nextLng',
    },

    interpolation: {
      escapeValue: false, // React already escapes
    },

    // Debug mode in development
    debug: import.meta.env.DEV,
  });

// Update HTML lang attribute when language changes
// This affects native browser elements like <input type="date">
const updateHtmlLang = (lang: string) => {
  document.documentElement.lang = lang;
};

// Set initial lang attribute
updateHtmlLang(i18n.language);

// Listen for language changes
i18n.on('languageChanged', updateHtmlLang);

export default i18n;

/**
 * Helper to change language and optionally persist to backend
 */
export async function changeLanguage(lang: SupportedLanguage, persistToBackend = true) {
  await i18n.changeLanguage(lang);

  if (persistToBackend) {
    try {
      const { api } = await import('../services/api');
      await api.patch('/auth/preferences', { language: lang });
    } catch {
      // Silently fail - localStorage is enough for now
    }
  }
}

/**
 * Initialize language from user preferences (call after login/checkAuth)
 */
export function initLanguageFromPreferences(language?: string) {
  if (language && SUPPORTED_LANGUAGES.some(l => l.code === language)) {
    i18n.changeLanguage(language);
  }
}
